// @flow

import { declareAction, declareAtom } from '@reatom/core'
import axios from 'axios'

import * as common from '@bfg/common'
import { PaymentAtom, SetPaymentProcessor, SuccessfulPayment, UnSuccessfulPayment } from '@bfg/atoms/Payment'
import { PaywallVariantAtom } from '@bfg/atoms/PaywallVariant'
import { AppConfigAtom } from '@bfg/atoms/AppConfig'
import { gtagEventNew } from '@bfg/utils/gtag'
import { UtmAnalytics } from '@bfg/atoms/UtmAnalytics'
import { SubscriberAtom, SubscriberLoaded } from '@bfg/atoms/Subscriber'
import { SkipStepExperimentEnum } from '@bfg/enums/SkipStepExperimentEnum'
import { NRError, NRSubscribed, NRSuccess } from '@bfg/atoms/NewRelic'

export const CheckApplePaySupport = declareAction(
  'checkApplePaySupportAction',
  (payload, store) => {
    store.dispatch(StoreAdditionalData({ ...payload }))
    const appConfig = store.getState(AppConfigAtom)
    if (window.ApplePaySession) {
      const canMakePayments = ApplePaySession.canMakePayments()
      if (canMakePayments) {
        store.dispatch(ApplePaySupportEnabled())
        const data = new FormData()
        data.append('paymentTypeId', payload.paymentTypeId)
        axios.post(
          `${common.api}/payment-request/${appConfig.gateway.aPay}/apple/subscription`,
          data
        )
          .then(response => {
            if (response.status === 200) {
              const paymentRequestObject = response.data
              store.dispatch(StorePaymentRequest(paymentRequestObject))
            } else {
            }
          })
          .catch(console.error)
      }
    }
  }
)

export const ShowApplePaymentRequest = declareAction(
  'showApplePayRequestAction',
  (payload, store) => {
    const state = store.getState(ApplePaymentAtom)
    const paymentRequestObject = state.paymentRequest
    const { paymentTypeId: paymentTypeId, experiments: experiments } = state.additionalData
    const appleSession = new ApplePaySession(2, paymentRequestObject)
    if (appleSession !== null) {
      appleSession.onvalidatemerchant = (event) => {
        store.dispatch(ValidateMerchantAction({validationUrl: event.validationURL, paymentTypeId}))
      }
      appleSession.onpaymentauthorized = (event) => {
        const email = event.payment.shippingContact.emailAddress
        store.dispatch(ProcessPayment({
          paymentData: { token: event.payment, email: email },
          paymentTypeId: paymentTypeId,
          experiments: experiments,
        }))
      }
      store.dispatch(StoreSession(appleSession))
      appleSession.begin()
    } else {
    }
  }
)

const ApplePaySupportEnabled = declareAction('applePaySupportedAction')
const StorePaymentRequest = declareAction('storePaymentRequestAction')
const StoreSession = declareAction('storeSessionAction')
const ValidateMerchantAction = declareAction(
  'validateMerchantAction',
  (payload, store) => {
    const state = store.getState(ApplePaymentAtom)
    const session = state.session
    const form = new FormData()
    form.append('validationUrl', payload.validationUrl)
    form.append('paymentTypeId', payload.paymentTypeId)
    axios.post(
      `${common.api}/validate-merchant/apple/bluesnap`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )
      .then(response => {
          if (response.status === 200) {
            session.completeMerchantValidation(JSON.parse(response.data.token))
          }
        }
      )
      .catch(console.error)
  }
)

const ProcessPayment = declareAction(
  'processPaymentAction',
  (payload, store) => {
    const appState = store.getState(AppConfigAtom)
    const state = store.getState(ApplePaymentAtom)
    const session = state.session
    const utmState = store.getState(UtmAnalytics)
    const paymentState = store.getState(PaymentAtom)
    const subscriberState = store.getState(SubscriberAtom)

    const skipStepExperiment = payload.experiments.skipStepExperiment
    const version = payload.experiments.generatorExperiment

    const stepSkipExperimentData = {
      experimentName: 'stepSkip',
      experimentValue: skipStepExperiment === SkipStepExperimentEnum.skip
        ? 'flow-3-step'
        : 'flow-4-step',
    }

    const paymentToken = b64EncodeUnicode(JSON.stringify(payload.paymentData.token));
    // const paymentToken = Base64.encode(JSON.stringify(payload.paymentData.token));
    // const paymentToken = JSON.stringify({token:payload.paymentData.token.token})

    const data = {
      token: paymentToken,
      paymentTypeId: payload.paymentTypeId,
      processor: 'APPLE',
      gateway: paymentState.gateway,
      utmSource: utmState.utmSource,
      utmMedium: utmState.utmMedium,
      utmCampaign: utmState.utmCampaign,
      version: version,
      email: payload.paymentData.email,
      experiments: [
        stepSkipExperimentData
      ],
      gclid: utmState.gclid,
      paywallVariant: store.getState(PaywallVariantAtom).variant,
    }
    localStorage.setItem('payeremail', payload.paymentData.email)

    // const data = {
    //   token: payload
    // }
    // const paymentToken = b64EncodeUnicode(JSON.stringify(payload.token));
    // const paymentToken = Base64.encode(JSON.stringify(payload.token));
    axios.post(
      `${common.api}/${appState.gateway.aPay}/subscriber/${appState.fingerprint}/subscribe/apple`,
      data
    )
      .then(response => {
          if (response.data.state === 'active') {
            session.completePayment(ApplePaySession.STATUS_SUCCESS)
            const newSubscription = { ...response.data }
            delete newSubscription.subscriber
            const newSubscriptionsList = [...subscriberState.subscriber.subscriptions]
            newSubscriptionsList.push(newSubscription)
            store.dispatch(SubscriberLoaded({ ...subscriberState.subscriber, subscriptions: newSubscriptionsList }))
            session.completePayment(ApplePaySession.STATUS_SUCCESS)
            store.dispatch(SetPaymentProcessor('APPLE'))
            store.dispatch(SuccessfulPayment({ date: response.data.expireDate, experiment: skipStepExperiment }))
            // store.dispatch(FBPTrack(['Lead']));
            const eventShort = 'applepay_subscription_monthly_new'
            gtagEventNew(eventShort)
            if (window.fbq && typeof window.fbq === 'function') {
              window.fbq('track', 'BFGconversion')
            }
            if (window.ttq) {
              window.ttq.track('Subscribe')
              window.ttq.track('BFGconversion')
            }
            store.dispatch(NRSubscribed(NRSuccess))
          } else {
            const container = document.getElementById('apple-pay-button')
            if (container) {
              container.style.display = 'initial'
            }
            session.completePayment(ApplePaySession.STATUS_FAILURE)
            store.dispatch(UnSuccessfulPayment())
            store.dispatch(NRSubscribed(NRError))
          }
        }
      )
      .catch(console.error)
  }
)

const b64EncodeUnicode = str => {
  return btoa(encodeURIComponent(str).replace(
      /%([0-9A-F]{2})/g,
      function toSolidBytes (match, p1) {
        return String.fromCharCode('0x' + p1)
      }
    )
  )
}

const StoreAdditionalData = declareAction('storeAdditionalDataAction')

export const ApplePaymentAtom = declareAtom(
  'applePaymentAtom',
  {
    applePayEnabled: false,
    paymentRequest: null,
    session: null,
    additionalData: {},
  },
  on => [
    on(ApplePaySupportEnabled, (state, payload) => {
      return { ...state, applePayEnabled: true }
    }),
    on(StorePaymentRequest, (state, payload) => {
      return { ...state, paymentRequest: payload }
    }),
    on(StoreSession, (state, payload) => {
      return { ...state, session: payload }
    }),
    on(StoreAdditionalData, (state, payload) => {
      return { ...state, additionalData: { ...state.additionalData, ...payload } }
    })
  ]
)